/**
 * Splash Screen Module
 * Handles the intro animation on first page load
 */

const SPLASH_ANIMATION_ENABLED = true;

interface SplashScreenState {
  element: HTMLElement | null;
  logo: HTMLElement | null;
  hasPlayed: boolean;
  autoPlayTimeout: ReturnType<typeof setTimeout> | null;
  isAnimating: boolean;
}

const state: SplashScreenState = {
  element: null,
  logo: null,
  hasPlayed: false,
  autoPlayTimeout: null,
  isAnimating: false,
};

function removeImmediately(): void {
  const element = document.getElementById('splash-screen');
  if (element) {
    element.classList.add('is-hidden');
  }
  document.documentElement.classList.remove('splash-active');
  document.body.classList.remove('splash-active');
}

function startAnimation(): void {
  if (state.hasPlayed || state.isAnimating || !state.element) return;

  state.isAnimating = true;
  state.hasPlayed = true;

  // Clear auto-play timeout if user triggered
  if (state.autoPlayTimeout) {
    clearTimeout(state.autoPlayTimeout);
  }

  // Mark as shown in session
  sessionStorage.setItem('splashShown', 'true');

  // Start the animation
  state.element.classList.add('is-animating');

  // Remove splash screen and splash-active together after animation completes
  setTimeout(() => {
    if (state.element) {
      state.element.classList.add('is-hidden');
    }
    document.documentElement.classList.remove('splash-active');
    document.body.classList.remove('splash-active');
    state.isAnimating = false;
  }, 1000);
}

function bindEvents(): void {
  const skipEvents: Array<keyof DocumentEventMap> = ['click', 'touchstart', 'wheel', 'keydown'];

  skipEvents.forEach(eventType => {
    document.addEventListener(eventType, (e: Event) => {
      // Don't skip if animation already started or finished
      if (state.isAnimating || state.hasPlayed) return;

      // Allow normal behavior for interactive elements
      if (eventType === 'keydown') {
        const keyEvent = e as KeyboardEvent;
        if (!['Escape', 'Enter', ' '].includes(keyEvent.key)) return;
      }

      startAnimation();
    }, { once: false, passive: true });
  });
}

export function initSplashScreen(): void {
  // Check if splash animation is enabled
  if (!SPLASH_ANIMATION_ENABLED) {
    removeImmediately();
    return;
  }

  // Check if already shown this session
  if (sessionStorage.getItem('splashShown') === 'true') {
    removeImmediately();
    return;
  }

  state.element = document.getElementById('splash-screen');
  if (!state.element) {
    // On subpages: set flag so animation doesn't play when going to homepage later
    sessionStorage.setItem('splashShown', 'true');
    return;
  }

  state.logo = state.element.querySelector('.splash-screen__logo');

  // Add splash-active class to html and body to hide header elements
  document.documentElement.classList.add('splash-active');
  document.body.classList.add('splash-active');

  // Bind event handlers
  bindEvents();

  // Auto-start animation after delay
  state.autoPlayTimeout = setTimeout(() => {
    startAnimation();
  }, 2500);
}

// Initialize immediately to prevent flash of content
export function initSplashScreenEarly(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSplashScreen());
  } else {
    initSplashScreen();
  }
}
